/**
 * User Feedback Service
 * Collect, store, and analyze user feedback for continuous improvement
 */

import { db } from './firebase';
import { collection, addDoc, serverTimestamp, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { analytics, AnalyticsEvent } from './analytics';

export enum FeedbackType {
  BUG = 'bug',
  FEATURE_REQUEST = 'feature_request',
  IMPROVEMENT = 'improvement',
  GENERAL = 'general',
  UX_ISSUE = 'ux_issue',
  PERFORMANCE = 'performance',
}

export enum FeedbackPriority {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical',
}

export enum FeedbackSentiment {
  VERY_NEGATIVE = 'very_negative',
  NEGATIVE = 'negative',
  NEUTRAL = 'neutral',
  POSITIVE = 'positive',
  VERY_POSITIVE = 'very_positive',
}

export interface FeedbackData {
  type: FeedbackType;
  category?: string;
  message: string;
  sentiment?: FeedbackSentiment;
  rating?: number; // 1-5
  email?: string;
  allowContact?: boolean;
  
  // Context data
  userId?: string;
  userEmail?: string;
  page?: string;
  pageTitle?: string;
  url?: string;
  timestamp: any;
  
  // Technical context
  browser?: string;
  os?: string;
  screenSize?: string;
  deviceType?: 'mobile' | 'tablet' | 'desktop';
  
  // Application context
  feature?: string;
  previousPage?: string;
  sessionDuration?: number;
  
  // Attachments/screenshots (future)
  screenshot?: string;
  attachments?: string[];
  
  // Status tracking
  status: 'new' | 'in_review' | 'planned' | 'in_progress' | 'completed' | 'wont_fix';
  priority?: FeedbackPriority;
  assignedTo?: string;
  
  // Metadata
  userAgent?: string;
  ipAddress?: string; // Anonymized
  language?: string;
}

class FeedbackService {
  private sessionStartTime: number = Date.now();
  private previousPage: string = '';

  /**
   * Submit user feedback
   */
  async submitFeedback(feedback: Partial<FeedbackData>): Promise<string> {
    try {
      // Collect context automatically
      const contextData = this.collectContext();
      
      const feedbackDoc: FeedbackData = {
        type: feedback.type || FeedbackType.GENERAL,
        category: feedback.category,
        message: feedback.message || '',
        sentiment: feedback.sentiment,
        rating: feedback.rating,
        email: feedback.email,
        allowContact: feedback.allowContact || false,
        
        // Auto-collected context
        ...contextData,
        
        // User-provided data
        userId: feedback.userId,
        userEmail: feedback.userEmail,
        feature: feedback.feature,
        
        // Status
        status: 'new',
        priority: this.calculatePriority(feedback),
        
        timestamp: serverTimestamp(),
      };

      // Save to Firestore
      const docRef = await addDoc(collection(db, 'feedback'), feedbackDoc);

      // Track in analytics
      analytics.trackEvent(AnalyticsEvent.FEATURE_INTERACTION, {
        feature_name: 'feedback',
        action: 'submit',
        feedback_type: feedback.type,
        sentiment: feedback.sentiment,
        rating: feedback.rating,
        feedback_id: docRef.id,
      });

      return docRef.id;
    } catch (error) {
      console.error('Error submitting feedback:', error);
      analytics.trackError(error as Error, false, {
        context: 'feedback_submission',
      });
      throw error;
    }
  }

  /**
   * Submit quick rating (1-5 stars)
   */
  async submitQuickRating(rating: number, _page: string, feature?: string): Promise<string> {
    const sentiment = this.ratingToSentiment(rating);
    
    return this.submitFeedback({
      type: FeedbackType.GENERAL,
      rating,
      sentiment,
      message: `Quick rating: ${rating}/5 stars`,
      feature,
    });
  }

  /**
   * Submit NPS score (Net Promoter Score)
   */
  async submitNPS(score: number, comment?: string): Promise<string> {
    const sentiment = score >= 9 ? FeedbackSentiment.VERY_POSITIVE :
                      score >= 7 ? FeedbackSentiment.POSITIVE :
                      score >= 5 ? FeedbackSentiment.NEUTRAL :
                      score >= 3 ? FeedbackSentiment.NEGATIVE :
                      FeedbackSentiment.VERY_NEGATIVE;

    return this.submitFeedback({
      type: FeedbackType.GENERAL,
      category: 'nps',
      rating: score,
      sentiment,
      message: comment || `NPS Score: ${score}/10`,
    });
  }

  /**
   * Report a bug with auto-collected context
   */
  async reportBug(description: string, steps?: string, expected?: string): Promise<string> {
    const message = `
**Description:** ${description}

${steps ? `**Steps to Reproduce:**\n${steps}\n` : ''}
${expected ? `**Expected Behavior:**\n${expected}\n` : ''}

**Technical Details:**
- Browser: ${this.getBrowserInfo()}
- Screen: ${window.innerWidth}x${window.innerHeight}
- URL: ${window.location.href}
    `.trim();

    return this.submitFeedback({
      type: FeedbackType.BUG,
      message,
      priority: FeedbackPriority.HIGH,
    });
  }

  /**
   * Request a feature
   */
  async requestFeature(title: string, description: string, useCase?: string): Promise<string> {
    const message = `
**Feature:** ${title}

**Description:** ${description}

${useCase ? `**Use Case:**\n${useCase}` : ''}
    `.trim();

    return this.submitFeedback({
      type: FeedbackType.FEATURE_REQUEST,
      message,
    });
  }

  /**
   * Report UX/usability issue
   */
  async reportUXIssue(issue: string, suggestion?: string): Promise<string> {
    const message = `
**Issue:** ${issue}

${suggestion ? `**Suggestion:**\n${suggestion}` : ''}
    `.trim();

    return this.submitFeedback({
      type: FeedbackType.UX_ISSUE,
      message,
      priority: FeedbackPriority.MEDIUM,
    });
  }

  /**
   * Get recent feedback for analytics
   */
  async getRecentFeedback(userId?: string, limitCount = 10): Promise<FeedbackData[]> {
    try {
      let q = query(
        collection(db, 'feedback'),
        orderBy('timestamp', 'desc'),
        limit(limitCount)
      );

      if (userId) {
        q = query(
          collection(db, 'feedback'),
          where('userId', '==', userId),
          orderBy('timestamp', 'desc'),
          limit(limitCount)
        );
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as any));
    } catch (error) {
      console.error('Error fetching feedback:', error);
      return [];
    }
  }

  /**
   * Get feedback statistics
   */
  async getFeedbackStats(): Promise<{
    total: number;
    byType: Record<string, number>;
    bySentiment: Record<string, number>;
    byPriority: Record<string, number>;
    averageRating: number;
  }> {
    try {
      const snapshot = await getDocs(collection(db, 'feedback'));
      const feedbackList = snapshot.docs.map(doc => doc.data() as FeedbackData);

      const byType: Record<string, number> = {};
      const bySentiment: Record<string, number> = {};
      const byPriority: Record<string, number> = {};
      let totalRating = 0;
      let ratingCount = 0;

      feedbackList.forEach(fb => {
        byType[fb.type] = (byType[fb.type] || 0) + 1;
        if (fb.sentiment) {
          bySentiment[fb.sentiment] = (bySentiment[fb.sentiment] || 0) + 1;
        }
        if (fb.priority) {
          byPriority[fb.priority] = (byPriority[fb.priority] || 0) + 1;
        }
        if (fb.rating) {
          totalRating += fb.rating;
          ratingCount++;
        }
      });

      return {
        total: feedbackList.length,
        byType,
        bySentiment,
        byPriority,
        averageRating: ratingCount > 0 ? totalRating / ratingCount : 0,
      };
    } catch (error) {
      console.error('Error getting feedback stats:', error);
      return {
        total: 0,
        byType: {},
        bySentiment: {},
        byPriority: {},
        averageRating: 0,
      };
    }
  }

  /**
   * Collect context data automatically
   */
  private collectContext(): Partial<FeedbackData> {
    return {
      page: window.location.pathname,
      pageTitle: document.title,
      url: window.location.href,
      browser: this.getBrowserInfo(),
      os: this.getOSInfo(),
      screenSize: `${window.innerWidth}x${window.innerHeight}`,
      deviceType: this.getDeviceType(),
      userAgent: navigator.userAgent,
      language: navigator.language,
      previousPage: this.previousPage,
      sessionDuration: Date.now() - this.sessionStartTime,
    };
  }

  /**
   * Calculate priority based on feedback type and sentiment
   */
  private calculatePriority(feedback: Partial<FeedbackData>): FeedbackPriority {
    if (feedback.priority) return feedback.priority;

    // Auto-prioritize based on type
    if (feedback.type === FeedbackType.BUG) {
      if (feedback.sentiment === FeedbackSentiment.VERY_NEGATIVE) {
        return FeedbackPriority.CRITICAL;
      }
      return FeedbackPriority.HIGH;
    }

    if (feedback.type === FeedbackType.PERFORMANCE) {
      return FeedbackPriority.HIGH;
    }

    if (feedback.type === FeedbackType.UX_ISSUE) {
      return FeedbackPriority.MEDIUM;
    }

    return FeedbackPriority.LOW;
  }

  /**
   * Convert rating to sentiment
   */
  private ratingToSentiment(rating: number): FeedbackSentiment {
    if (rating === 5) return FeedbackSentiment.VERY_POSITIVE;
    if (rating === 4) return FeedbackSentiment.POSITIVE;
    if (rating === 3) return FeedbackSentiment.NEUTRAL;
    if (rating === 2) return FeedbackSentiment.NEGATIVE;
    return FeedbackSentiment.VERY_NEGATIVE;
  }

  /**
   * Get browser information
   */
  private getBrowserInfo(): string {
    const ua = navigator.userAgent;
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Safari')) return 'Safari';
    if (ua.includes('Edge')) return 'Edge';
    return 'Unknown';
  }

  /**
   * Get OS information
   */
  private getOSInfo(): string {
    const ua = navigator.userAgent;
    if (ua.includes('Win')) return 'Windows';
    if (ua.includes('Mac')) return 'MacOS';
    if (ua.includes('Linux')) return 'Linux';
    if (ua.includes('Android')) return 'Android';
    if (ua.includes('iOS')) return 'iOS';
    return 'Unknown';
  }

  /**
   * Get device type
   */
  private getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
    const width = window.innerWidth;
    if (width < 768) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
  }

  /**
   * Update previous page for context
   */
  setPreviousPage(page: string) {
    this.previousPage = page;
  }
}

// Export singleton instance
export const feedbackService = new FeedbackService();

export default feedbackService;
